import { useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import VideoCard from '@/components/feature/VideoCard';
import PlayerPanel from '@/pages/detail/components/PlayerPanel';
import { videoList, typeLabels } from '@/mocks/videos';
import { getRelated, formatCount } from '@/utils/video';

export default function DetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const video = useMemo(() => videoList.find((item) => item.id === id), [id]);
  const related = useMemo(() => (video ? getRelated(video, videoList) : []), [video]);

  useEffect(() => {
    if (video) {
      document.title = `${video.title} - 在线观看 - 在线免费观看`;
    }
  }, [video]);

  if (!video) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${video.type}`} className="hover:text-primary-700 transition-colors cursor-pointer">
            {typeLabels[video.type]}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{video.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-[260px] flex-shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={video.cover}
                alt={`${video.title} 封面大图`}
                title={`${video.title} - 在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">{video.title}</h1>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium">
                {typeLabels[video.type]}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{video.subType}</span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{video.region}</span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{video.year}</span>
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold flex items-center gap-1">
                <i className="ri-star-fill text-[10px]"></i>
                {video.rating.toFixed(1)} 分
              </span>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-foreground-600 mb-5">
              <div className="flex gap-2">
                <dt className="text-foreground-500 flex-shrink-0">导演</dt>
                <dd>{video.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-500 flex-shrink-0">主演</dt>
                <dd>{video.cast.join('、')}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-500 flex-shrink-0">状态</dt>
                <dd>{video.status}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-500 flex-shrink-0">播放量</dt>
                <dd>{formatCount(video.views)}</dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-2 mb-5">
              {video.genres.map((genre) => (
                <span key={genre} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  {genre}
                </span>
              ))}
            </div>

            <p className="text-sm text-foreground-700 leading-relaxed mb-8">{video.description}</p>

            <PlayerPanel video={video} />
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
              <a href="#related" className="cursor-pointer">相关推荐</a>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}